/**
 * Whether this page load has already re-read the signed-in user from the
 * server. See `hooks/useSessionBootstrap.ts` for why that matters.
 *
 * Kept in its own dependency-free module on purpose: the hook needs the auth
 * slice (for `getMe`), and the auth slice needs to set these flags on login and
 * clear them on logout. Importing the hook from the slice would close that into
 * a cycle, which happens to work under ESM but only because nothing touches the
 * bindings at module scope — a fragile thing to rely on. Nothing imports this
 * file but those two.
 *
 * State outside React is deliberate too. The scope being modelled is "this page
 * load", which outlives every component that asks about it; a remount must not
 * trigger a second fetch.
 */

let hasRun = false;
let settled = false;

/** True once the session has been confirmed (or the attempt has given up). */
export const isSessionSettled = () => settled;

/** True once a fetch has been started, so a remount does not start another. */
export const hasSessionFetchStarted = () => hasRun;

export const markSessionFetchStarted = () => {
  hasRun = true;
};

export const markSessionSettled = () => {
  settled = true;
};

/**
 * Records that the session is already current, so the shell renders without a
 * fetch. Called by the login thunk: its response IS a fresh read of the user,
 * so re-reading it a moment later would only put a spinner between the login
 * form and the dashboard.
 */
export const markSessionBootstrapped = () => {
  hasRun = true;
  settled = true;
};

/**
 * Forces the next session to re-sync. Called on sign-out, because a different
 * account can sign in within the same page load and must not inherit the
 * previous one's "already current" flag.
 */
export const resetSessionBootstrap = () => {
  hasRun = false;
  settled = false;
};
